import React from 'react';
import styled from 'styled-components';
import Button from 'components/UI/Button';
import RadioButton from 'components/UI/RadioButton';
import SelectComponent, { Option } from 'components/UI/Select';
import RecommendationProductPicker from './RecommendationProductPicker';
import RecommendationProductsTable from './RecommendationProductsTable';
import { RecommendationDisplayMode, RecommendationSection } from 'data/Product';
import { DISPLAY_MODE, DISPLAY_MODE_LABELS, RECOMMENDATION_STRINGS } from 'constants/productRecommendations';
import { Border, Main, Text } from 'styles/themeColors';

const Card = styled.div`
  border: 1px solid ${Border.main};
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  background: ${Main.white};
`;

const FieldsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const Field = styled.div<{ $grow?: boolean }>`
  flex: ${({ $grow }) => ($grow ? '1 1 320px' : '0 1 auto')};
  min-width: 0;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
  color: ${Text.primary};
`;

const Asterisk = styled.span`
  color: ${Main.error};
  margin-left: 2px;
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 40px;
`;

const ErrorText = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: ${Main.error};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

export interface SectionEditorErrors {
  title?: string;
  products?: string;
}

interface Props {
  draft: RecommendationSection;
  isNew: boolean;
  titleOptions: Option[];
  errors: SectionEditorErrors;
  onChange: (draft: RecommendationSection) => void;
  onSave: () => void;
  onCancel: () => void;
  vendorId?: number;
  excludeCode?: string;
}

const RecommendationSectionEditor = ({
  draft,
  isNew,
  titleOptions,
  errors,
  onChange,
  onSave,
  onCancel,
  vendorId,
  excludeCode,
}: Props) => {
  const setDisplayMode = (displayMode: RecommendationDisplayMode) => onChange({ ...draft, displayMode });

  return (
    <Card>
      <FieldsRow>
        <Field>
          <FieldLabel>
            {RECOMMENDATION_STRINGS.SECTION_TITLE_LABEL}
            <Asterisk>*</Asterisk>
          </FieldLabel>
          <SelectComponent
            id="recommendation-section-title"
            value={draft.titleId ? String(draft.titleId) : ''}
            options={titleOptions}
            onChange={event => onChange({ ...draft, titleId: event.target.value ? Number(event.target.value) : null })}
            placeholder={RECOMMENDATION_STRINGS.SECTION_TITLE_PLACEHOLDER}
            isInvalid={!!errors.title}
            errorMessage={errors.title}
            errroLabelPostion="bottom"
            width="280px"
          />
        </Field>

        <Field>
          <FieldLabel>{RECOMMENDATION_STRINGS.DISPLAY_MODE_LABEL}</FieldLabel>
          <RadioRow>
            {(Object.keys(DISPLAY_MODE) as RecommendationDisplayMode[]).map(mode => (
              <RadioButton
                key={mode}
                id={`recommendation-display-mode-${mode}`}
                name="recommendation-display-mode"
                value={mode}
                checked={draft.displayMode === mode}
                label={DISPLAY_MODE_LABELS[mode]}
                onChange={checked => checked && setDisplayMode(mode)}
              />
            ))}
          </RadioRow>
        </Field>

        <Field $grow>
          <FieldLabel>{RECOMMENDATION_STRINGS.MANUAL_ADD_LABEL}</FieldLabel>
          <RecommendationProductPicker
            value={draft.items}
            onChange={items => onChange({ ...draft, items })}
            vendorId={vendorId}
            excludeCode={excludeCode}
          />
        </Field>
      </FieldsRow>

      <RecommendationProductsTable
        items={draft.items}
        onChange={items => onChange({ ...draft, items })}
        droppableId={`recommendation-items-${draft.id ?? 'new'}`}
      />
      {errors.products && <ErrorText>{errors.products}</ErrorText>}

      <Actions>
        <Button id="recommendation-section-cancel" theme="brandDark" bgColor={Main.white} color={Main.brandDark} onClick={onCancel}>
          {RECOMMENDATION_STRINGS.CANCEL}
        </Button>
        <Button id="recommendation-section-save" theme="brandDark" onClick={onSave}>
          {isNew ? RECOMMENDATION_STRINGS.SAVE_NEW : RECOMMENDATION_STRINGS.SAVE_EDIT}
        </Button>
      </Actions>
    </Card>
  );
};

export default RecommendationSectionEditor;
